import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { money, clampSeats, presetFor } from "../lib/pricing";
import { Minus, Plus, Users, Check, LockKey } from "@phosphor-icons/react";

// Interactive matrix: preset chips + seats stepper + add-on switches. Totals are rendered by the parent (see computeQuote).
export default function PlanBuilder({ catalog, value, onChange, minSeats = 1, highlight, disabled = false, lockedRemovals = [], grandfathered = [], trials = {} }) {
  const config = catalog?.config || {};
  const activePreset = presetFor(catalog, value.features);
  const set = (patch) => onChange({ ...value, ...patch });
  const floor = Math.max(config.min_seats || 1, minSeats);

  const choosePreset = (p) => set({ presetId: p.id, features: [...p.feature_keys], seats: Math.max(floor, p.included_seats) });
  const toggleFeature = (key, on) => {
    const next = on ? [...new Set([...value.features, key])] : value.features.filter((k) => k !== key);
    set({ features: next, presetId: presetFor(catalog, next)?.id || "custom" });
  };
  const setSeats = (n) => set({ seats: Math.max(floor, clampSeats(config, n)) });

  return (
    <div className="space-y-6" data-testid="plan-builder">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Start from a bundle</p>
        <div className="flex flex-wrap gap-2" data-testid="plan-builder-presets">
          {(catalog?.presets || []).map((p) => {
            const on = activePreset?.id === p.id;
            return (
              <button key={p.id} type="button" disabled={disabled} onClick={() => choosePreset(p)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:border-primary/50"}`}
                data-testid={`plan-builder-preset-${p.id}`}>
                {p.name}
                {p.bundle_discount_pct > 0 && <span className={`ml-1.5 text-[10px] font-bold ${on ? "text-primary-foreground/80" : "text-emerald-600"}`}>−{p.bundle_discount_pct}%</span>}
              </button>
            );
          })}
          <span className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold ${!activePreset ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-dashed"}`} data-testid="plan-builder-preset-custom">Custom</span>
        </div>
        {activePreset?.bundle_discount_pct > 0 && (
          <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-2" data-testid="plan-builder-bundle-note">
            {activePreset.name} bundle: {activePreset.bundle_discount_pct}% off the whole basket. Add as many users as you like — change the add-ons and it becomes a custom plan at list price.
          </p>
        )}
      </div>

      <div className="grid sm:grid-cols-[1fr_auto] gap-4 items-end border border-border rounded-lg p-4 bg-card">
        <div>
          <p className="text-xs font-semibold flex items-center gap-1.5"><Users size={14} /> Team members with a login</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{money(config.per_user_monthly, { compact: true })} per user / month. Clients and suppliers using their portals are free.</p>
          {minSeats > (config.min_seats || 1) && <p className="text-[11px] text-amber-600 mt-1" data-testid="plan-builder-min-seats">You currently have {minSeats} team members — deactivate users to go lower.</p>}
        </div>
        <div className="flex items-center gap-1" data-testid="plan-builder-seats">
          <button type="button" disabled={disabled || value.seats <= floor} onClick={() => setSeats(value.seats - 1)} className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40" data-testid="plan-builder-seats-minus"><Minus size={14} /></button>
          <Input type="number" min={floor} max={config.max_seats || 500} value={value.seats} disabled={disabled}
            onChange={(e) => set({ seats: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
            onBlur={() => setSeats(value.seats)} className="h-9 w-20 text-center font-bold" data-testid="plan-builder-seats-input" />
          <button type="button" disabled={disabled} onClick={() => setSeats((parseInt(value.seats, 10) || floor) + 1)} className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:bg-muted disabled:opacity-40" data-testid="plan-builder-seats-plus"><Plus size={14} /></button>
        </div>
      </div>

      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Add-ons</p>
        <div className="grid md:grid-cols-2 gap-2" data-testid="plan-builder-addons">
          {(catalog?.addons || []).filter((a) => a.sellable !== false).map((a) => {
            const on = value.features.includes(a.key);
            const locked = lockedRemovals.includes(a.key);
            return (
              <label key={a.key} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${on ? "border-primary/40 bg-primary/5" : "border-border bg-card hover:border-primary/30"} ${highlight === a.key ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""}`} data-testid={`plan-builder-addon-${a.key}`}>
                <Switch checked={on} disabled={disabled || (locked && on)} onCheckedChange={(v) => toggleFeature(a.key, v)} className="mt-0.5" data-testid={`plan-builder-addon-toggle-${a.key}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{a.label}</span>
                    <span className="text-xs font-bold whitespace-nowrap">{money(a.monthly_price, { compact: true })}<span className="text-muted-foreground font-normal">/mo</span></span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{a.blurb}</p>
                  {locked && on && <Badge variant="outline" className="text-[9px] mt-1 gap-1"><LockKey size={9} /> In use — contact support to remove</Badge>}
                  {!on && grandfathered.includes(a.key) && <Badge variant="outline" className="text-[9px] mt-1 gap-1" data-testid={`plan-builder-addon-${a.key}-grandfathered`}>Already included on your workspace at no charge</Badge>}
                  {!on && trials[a.key]?.status === "active" && <Badge variant="outline" className="text-[9px] mt-1 gap-1 border-amber-400 text-amber-700 dark:text-amber-400" data-testid={`plan-builder-addon-${a.key}-trial`}>Free trial — {trials[a.key].days_left} day{trials[a.key].days_left === 1 ? "" : "s"} left, add it to keep it</Badge>}
                  {!on && trials[a.key]?.status === "ended" && <Badge variant="outline" className="text-[9px] mt-1 gap-1 text-muted-foreground" data-testid={`plan-builder-addon-${a.key}-trial-ended`}>Trial ended {new Date(trials[a.key].ends_at).toLocaleDateString("en-GB")}</Badge>}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-border p-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Included in every plan</p>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {(catalog?.core || []).map((c) => (
            <span key={c.key} className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Check size={10} className="text-emerald-500" weight="bold" /> {c.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
